import React, { useState } from "react";
import { trpcClient } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useQueryClient } from "@tanstack/react-query";

import { toVnDateTimeLocal } from "@apv-erp/status";
interface ReportFinishedGoodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderDetailId: string;
  productionOrderId: string;
  productName: string;
  plannedQty: number;
  producedQty: number;
}

export function ReportFinishedGoodDialog({
  open,
  onOpenChange,
  orderDetailId,
  productionOrderId,
  productName,
  plannedQty,
  producedQty,
}: ReportFinishedGoodDialogProps) {
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState<number | "">("");
  const [machineId, setMachineId] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<string>("");
  const [date, setDate] = useState<string>(
    toVnDateTimeLocal(),
  );
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate remaining needed
  const remainingQty = Math.max(0, plannedQty - producedQty);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!quantity || quantity <= 0) {
      toast.error("Số lượng báo cáo phải lớn hơn 0");
      return;
    }
    if (!machineId) {
      toast.error("Vui lòng chọn Máy móc");
      return;
    }
    if (!employeeId) {
      toast.error("Vui lòng chọn Nhân viên");
      return;
    }

    setIsSubmitting(true);
    try {
      await trpcClient.productionOrders.reportFinishedGood.mutate({
        orderDetailId,
        productionOrderId,
        quantity: Number(quantity),
        machineId,
        employeeId,
        date: new Date(date).toISOString(),
        notes,
      });

      toast.success("Báo cáo thành phẩm thành công!");

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });

      // Also invalidate orders query if needed since order details are updated
      await queryClient.invalidateQueries({ queryKey: ["orders"] });

      // Close dialog & reset
      setQuantity("");
      setNotes("");
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Có lỗi xảy ra khi báo cáo thành phẩm");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Báo cáo Thành phẩm</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 py-4">
            <div className="bg-slate-50 p-3 rounded-md text-sm text-slate-700">
              <div>
                <strong>Sản phẩm:</strong> {productName}
              </div>
              <div className="flex gap-4 mt-1">
                <span>
                  SL Kế hoạch:{" "}
                  <span className="font-semibold">{plannedQty}</span>
                </span>
                <span>
                  Đã báo cáo:{" "}
                  <span className="font-semibold text-emerald-600">
                    {producedQty}
                  </span>
                </span>
                <span>
                  Cần thêm:{" "}
                  <span className="font-semibold text-blue-600">
                    {remainingQty}
                  </span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="quantity" className="text-right">
                Số lượng <span className="text-red-500">*</span>
              </Label>
              <Input
                id="quantity"
                type="number"
                className="col-span-3"
                value={quantity}
                onChange={(e) =>
                  setQuantity(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                min="1"
                required
              />
            </div>

            {quantity !== "" &&
              Number(quantity) > remainingQty &&
              remainingQty > 0 && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded ml-[115px]">
                  Cảnh báo: Số lượng báo cáo vượt quá số lượng cần thiết (
                  {remainingQty}).
                </div>
              )}

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="machineId" className="text-right">
                Máy <span className="text-red-500">*</span>
              </Label>
              <div className="col-span-3">
                <SharedCombobox
                  type="machine"
                  value={machineId}
                  onChange={setMachineId}
                  placeholder="Chọn máy..."
                />
              </div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="employeeId" className="text-right">
                Nhân viên <span className="text-red-500">*</span>
              </Label>
              <div className="col-span-3">
                <SharedCombobox
                  type="user"
                  params={{ scope: 'production' }} // nhân sự theo quy trình (03/11/2026)
                  value={employeeId}
                  onChange={setEmployeeId}
                  placeholder="Chọn nhân viên..."
                />
              </div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="date" className="text-right">
                Thời gian
              </Label>
              <Input
                id="date"
                type="datetime-local"
                className="col-span-3"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <Label htmlFor="notes" className="text-right mt-2">
                Ghi chú
              </Label>
              <Textarea
                id="notes"
                className="col-span-3"
                placeholder="Ghi chú thêm..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Đang xử lý..." : "Báo cáo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
